import React from 'react';
import { cn } from '../../../utils/cn';
import { inputVariants } from './Input.styles';
import { InputProps } from './Input.types';

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      containerClassName,
      labelClassName,
      inputSize,
      variant,
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      isRequired = false,
      isInvalid = false,
      isDisabled = false,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `${inputId}-error`;
    const helperTextId = `${inputId}-helper`;

    const showError = isInvalid || !!error;
    const computedVariant = showError ? 'error' : variant;

    return (
      <div className={cn('w-full', containerClassName)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'mb-1.5 block text-sm font-medium text-gray-700 dark:text-slate-300',
              labelClassName
            )}
          >
            {label}
            {isRequired && (
              <span className="ml-1 text-red-500" aria-label="required">
                *
              </span>
            )}
          </label>
        )}

        {/* Input Container */}
        <div className="relative">
          {/* Left Icon */}
          {leftIcon && (
            <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500">
              {leftIcon}
            </div>
          )}

          {/* Input Element */}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              inputVariants({ inputSize, variant: computedVariant }),
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              className
            )}
            disabled={isDisabled}
            aria-invalid={showError}
            aria-describedby={showError ? errorId : helperText ? helperTextId : undefined}
            aria-required={isRequired}
            {...props}
          />

          {/* Right Icon */}
          {rightIcon && (
            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500">
              {rightIcon}
            </div>
          )}
        </div>

        {/* Error Message */}
        {showError && error && (
          <p id={errorId} className="mt-1.5 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        {/* Helper Text */}
        {!showError && helperText && (
          <p id={helperTextId} className="mt-1.5 text-sm text-gray-500 dark:text-slate-400">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
